import Axios from 'axios';

import { GetTimestamp } from '@libs/NwMoment';
import { getBaseUrl, getCloudFilesContainerUrl } from '@libs/NwUtils';

import {
    GALLERY_MEDIA_SOURCE,
    GALLERY_MEDIA_TYPE,
    getGalleryVideoEmbedData,
} from '@containers/Art/Galleries/Galleries.constants';

export const ART_DOCUMENT_TYPE = {
    WEB_PHOTO: 'WebPhoto',
    WEB_VIDEO: 'WebVideo',
};

/** Numeric document types for `/documents/uploadsingle/{type}`. */
export const ART_DOCUMENT_UPLOAD_TYPE = {
    WEB_PHOTO: 5,
    WEB_VIDEO: 6,
    FILE: 7,
};

const documentCache = new Map();
const ART_PREVIEW_MEDIA_VARIANT_TYPES = ['Thumbnail', 'Cover', 'LinkedCover'];
const ART_PLAYBACK_MEDIA_VARIANT_TYPES = ['Standard', 'Video', 'LinkedVideo', 'HD', 'File'];
const ART_VIDEO_MEDIA_VARIANT_TYPES = ['Video', 'LinkedVideo'];
const VIDEO_FILE_EXTENSIONS = ['.mp4', '.mov', '.m4v', '.webm', '.ogv'];

const getCurrentUserId = () => {
    try {
        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        return currentUser?.UserID;
    } catch (error) {
        return undefined;
    }
};

const makeArtUploadBatchId = () => {
    const userId = getCurrentUserId() || '0';
    return `UPL_${userId}_${GetTimestamp()}`;
};

const normalizeDocumentResponse = (response) => {
    const data = response && response.data !== undefined ? response.data : response;
    if (Array.isArray(data)) {
        return data[0] || null;
    }
    return data || null;
};

const getDocumentIdFromPayload = (payload) => {
    if (!payload) return undefined;
    const documentId = payload.documentID ?? payload.DocumentID ?? payload.ID ?? payload.id;
    return documentId ? documentId : undefined;
};

const resolveUploadFile = (file) => {
    if (!file) return null;
    if (file instanceof File || file instanceof Blob) return file;
    return file.originFileObj || null;
};

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

/** Resolves document file paths returned by upload/GET APIs to a browser-ready URL. */
export const resolveArtDocumentFileUrl = (path) => {
    if (!path) return undefined;
    const trimmed = String(path).trim();
    if (!trimmed) return undefined;
    if (isAbsoluteUrl(trimmed)) return trimmed;

    const base = getCloudFilesContainerUrl();
    if (!base) return trimmed;

    return `${base.replace(/\/+$/, '')}/${trimmed.replace(/^\/+/, '')}`;
};

export const normalizeArtMediaType = (mediaType) => {
    const normalizedMediaType = String(mediaType || '').toLowerCase();
    if (normalizedMediaType === 'video' || normalizedMediaType === 'webvideo') {
        return GALLERY_MEDIA_TYPE.VIDEO;
    }
    if (normalizedMediaType === 'image' || normalizedMediaType === 'photo' || normalizedMediaType === 'webphoto') {
        return GALLERY_MEDIA_TYPE.IMAGE;
    }
    return undefined;
};

const getArtMediaVariants = (media = {}) => {
    const mediaList = media.Media || media.media;
    return Array.isArray(mediaList) ? mediaList : [];
};

const getArtMediaVariant = (variants = [], variantTypes = []) => {
    const normalizedVariantTypes = variantTypes.map((variantType) => String(variantType).toLowerCase());

    return variants.find((variant) =>
        normalizedVariantTypes.includes(String(variant.Type || variant.type || '').toLowerCase())
    );
};

const getArtMediaVariantUrl = (media = {}, variantTypes = []) => {
    const variant = getArtMediaVariant(getArtMediaVariants(media), variantTypes);
    return resolveArtDocumentFileUrl(variant?.Url || variant?.url);
};

const isVideoFileUrl = (url) => {
    const normalizedUrl = String(url || '').toLowerCase();
    return VIDEO_FILE_EXTENSIONS.some((extension) => normalizedUrl.includes(extension));
};

const isArtEmbedProvider = (provider) => {
    if (!provider || provider === 'CloudFiles') return false;
    const normalizedProvider = String(provider).toLowerCase();
    return normalizedProvider === 'youtube' || normalizedProvider === 'vimeo';
};

const getArtDocumentLinkUrl = (document) => {
    if (!document) return undefined;
    return document.Link ?? document.link ?? document.VideoUrl ?? document.videoUrl;
};

const getArtDocumentDirectPlaybackUrl = (document) => {
    if (!document) return undefined;
    if (document.Standard && document.Standard.Url) {
        return resolveArtDocumentFileUrl(document.Standard.Url);
    }
    if (document.HD && document.HD.Url) {
        return resolveArtDocumentFileUrl(document.HD.Url);
    }
    const mediaPlaybackUrl = getArtMediaVariantUrl(document, ART_PLAYBACK_MEDIA_VARIANT_TYPES);
    if (mediaPlaybackUrl) {
        return mediaPlaybackUrl;
    }
    const linkUrl = getArtDocumentLinkUrl(document);
    if (linkUrl) {
        return resolveArtDocumentFileUrl(linkUrl);
    }
    if (document.url) {
        return resolveArtDocumentFileUrl(document.url);
    }
    return undefined;
};

const getArtDocumentDirectPreviewUrl = (document) => {
    if (!document) return undefined;
    if (document.Thumbnail && document.Thumbnail.Url) {
        return resolveArtDocumentFileUrl(document.Thumbnail.Url);
    }
    if (document.thumbnailUrl) {
        return resolveArtDocumentFileUrl(document.thumbnailUrl);
    }

    const mediaPreviewUrl = getArtMediaVariantUrl(document, ART_PREVIEW_MEDIA_VARIANT_TYPES);
    if (mediaPreviewUrl) {
        return mediaPreviewUrl;
    }

    if (isArtEmbedProvider(document.Provider)) {
        const embedData = getGalleryVideoEmbedData(getArtDocumentDirectPlaybackUrl(document));
        if (embedData?.thumbnailUrl) {
            return embedData.thumbnailUrl;
        }
    }

    if (document.Standard && document.Standard.Url) {
        return resolveArtDocumentFileUrl(document.Standard.Url);
    }
    if (document.url) {
        return resolveArtDocumentFileUrl(document.url);
    }
    return undefined;
};

export const getArtStoredMediaType = (media = {}) => {
    const explicitType = normalizeArtMediaType(media.Type ?? media.type ?? media.MediaType ?? media.mediaType);
    if (explicitType) return explicitType;

    const source = media.Source || media.source;
    const provider = media.Provider || media.provider;
    if (source === GALLERY_MEDIA_SOURCE.EMBED || isArtEmbedProvider(provider)) {
        return GALLERY_MEDIA_TYPE.VIDEO;
    }

    const variants = getArtMediaVariants(media);
    if (getArtMediaVariant(variants, ART_VIDEO_MEDIA_VARIANT_TYPES)) {
        return GALLERY_MEDIA_TYPE.VIDEO;
    }

    const variantUrl =
        getArtMediaVariantUrl(media, ART_PLAYBACK_MEDIA_VARIANT_TYPES) ||
        getArtMediaVariantUrl(media, ART_PREVIEW_MEDIA_VARIANT_TYPES);
    const explicitUrl =
        media.Url ?? media.url ?? media.MediaUrl ?? media.mediaUrl ?? media.PlaybackUrl ?? media.playbackUrl;

    if (isVideoFileUrl(variantUrl) || isVideoFileUrl(explicitUrl)) {
        return GALLERY_MEDIA_TYPE.VIDEO;
    }

    if (variantUrl || explicitUrl || media.Thumbnail || media.thumbnail || media.Standard || media.standard) {
        return GALLERY_MEDIA_TYPE.IMAGE;
    }

    return undefined;
};

export const getArtStoredMediaUrl = (media = {}) => {
    const explicitUrl = resolveArtDocumentFileUrl(
        media.Url ?? media.url ?? media.MediaUrl ?? media.mediaUrl ?? media.PlaybackUrl ?? media.playbackUrl
    );
    if (explicitUrl) return explicitUrl;

    const mediaType = getArtStoredMediaType(media);
    if (mediaType === GALLERY_MEDIA_TYPE.VIDEO) {
        return (
            getArtMediaVariantUrl(media, ART_PLAYBACK_MEDIA_VARIANT_TYPES) ||
            getArtDocumentDirectPlaybackUrl(media) ||
            getArtMediaVariantUrl(media, ART_PREVIEW_MEDIA_VARIANT_TYPES) ||
            getArtDocumentDirectPreviewUrl(media)
        );
    }

    return (
        getArtMediaVariantUrl(media, ART_PREVIEW_MEDIA_VARIANT_TYPES) ||
        getArtDocumentDirectPreviewUrl(media) ||
        getArtMediaVariantUrl(media, ART_PLAYBACK_MEDIA_VARIANT_TYPES) ||
        getArtDocumentDirectPlaybackUrl(media)
    );
};

const inferDocumentType = (payload = {}) => {
    if (payload.Type) return payload.Type;
    if (payload.type === ART_DOCUMENT_TYPE.WEB_VIDEO || payload.type === 'Video') return 'Video';
    if (payload.type === ART_DOCUMENT_TYPE.WEB_PHOTO) return 'Photo';
    return payload.Type;
};

/** Normalizes GET document payloads and resolves cloud file paths. */
const normalizeFetchedDocument = (document) => {
    if (!document) return null;

    const documentId = getDocumentIdFromPayload(document);
    const thumbnailUrl = resolveArtDocumentFileUrl(document.Thumbnail?.Url ?? document.thumbnailUrl);
    const standardUrl = resolveArtDocumentFileUrl(
        document.Standard?.Url ?? document.url ?? getArtDocumentLinkUrl(document)
    );

    return {
        ...document,
        ...(documentId
            ? {
                  ID: documentId,
                  documentID: documentId,
              }
            : {}),
        ...(thumbnailUrl && {
            Thumbnail: {
                ...(document.Thumbnail || {}),
                Url: thumbnailUrl,
            },
        }),
        ...(standardUrl && {
            Standard: {
                ...(document.Standard || {}),
                Url: standardUrl,
            },
        }),
    };
};

/** Normalizes upload/link payloads to a document reference with both `ID` and `documentID`. */
const normalizeUploadedDocument = (payload) => {
    const documentId = getDocumentIdFromPayload(payload);
    if (!documentId) return null;

    const thumbnailUrl = resolveArtDocumentFileUrl(payload.thumbnailUrl ?? payload.Thumbnail?.Url);
    const standardUrl = resolveArtDocumentFileUrl(payload.url ?? payload.Standard?.Url);

    const inferredType = inferDocumentType(payload);

    return {
        ...payload,
        ID: documentId,
        documentID: documentId,
        ...(inferredType ? { Type: inferredType } : {}),
        ...(thumbnailUrl && {
            Thumbnail: {
                ...(payload.Thumbnail || {}),
                Url: thumbnailUrl,
            },
        }),
        ...(standardUrl && {
            Standard: {
                ...(payload.Standard || {}),
                Url: standardUrl,
            },
        }),
    };
};

export const getArtMediaDocumentId = (media = {}) => {
    return media.DocumentID ?? media.documentId ?? media.documentID ?? undefined;
};

export const getArtDocumentSource = (document) => {
    if (!document) return GALLERY_MEDIA_SOURCE.UPLOAD;

    if (isArtEmbedProvider(document.Provider)) {
        return GALLERY_MEDIA_SOURCE.EMBED;
    }

    if (document.ContactID) {
        return GALLERY_MEDIA_SOURCE.NETWALK;
    }

    return GALLERY_MEDIA_SOURCE.UPLOAD;
};

export const getArtDocumentMediaType = (document) => {
    if (!document) return GALLERY_MEDIA_TYPE.IMAGE;
    const normalizedDocumentType = normalizeArtMediaType(document.Type ?? document.type);
    if (normalizedDocumentType) return normalizedDocumentType;
    if (isArtEmbedProvider(document.Provider)) {
        return GALLERY_MEDIA_TYPE.VIDEO;
    }
    return GALLERY_MEDIA_TYPE.IMAGE;
};

/** Uploaded cloud videos still encoding have no Standard and no Thumbnail yet. */
export const isArtDocumentEncodingVideo = (document) => {
    if (!document) return false;
    if (getArtDocumentMediaType(document) !== GALLERY_MEDIA_TYPE.VIDEO) return false;
    if (getArtDocumentSource(document) !== GALLERY_MEDIA_SOURCE.UPLOAD) return false;

    const hasThumbnail = Boolean(document.Thumbnail?.Url || document.thumbnailUrl);
    const hasStandard = Boolean(document.Standard?.Url || document.url);

    return !hasThumbnail && !hasStandard;
};

export const getArtDocumentPlaybackUrl = (document) => {
    return getArtDocumentDirectPlaybackUrl(document);
};

export const getArtDocumentPreviewUrl = (document) => {
    return getArtDocumentDirectPreviewUrl(document);
};

export const getArtDocumentEmbedProvider = (document) => {
    if (!document || !document.Provider || document.Provider === 'CloudFiles') return undefined;
    return String(document.Provider).toLowerCase();
};

export const getArtDocumentDisplayName = (document, fallback = 'Media') => {
    if (!document) return fallback;
    if (document.PublicDescription) return document.PublicDescription;
    if (document.Title) return document.Title;
    return fallback;
};

export const getArtDocument = async (documentId, options = {}) => {
    const { useCache = true } = options;
    if (!documentId) return null;

    if (useCache && documentCache.has(documentId)) {
        return documentCache.get(documentId);
    }

    const response = await Axios.get(`/documents/${documentId}`);
    const document = normalizeFetchedDocument(normalizeDocumentResponse(response));
    if (document) {
        documentCache.set(documentId, document);
    }
    return document;
};

export const clearArtDocumentCache = (documentId) => {
    if (documentId) {
        documentCache.delete(documentId);
        return;
    }
    documentCache.clear();
};

/** @param {number} [explicitType] Overrides the video/photo inference (e.g. ART_DOCUMENT_UPLOAD_TYPE.FILE). */
export const getArtDocumentUploadNumericType = (file, explicitType) => {
    if (explicitType) return explicitType;
    return file && file.type && file.type.startsWith('video/')
        ? ART_DOCUMENT_UPLOAD_TYPE.WEB_VIDEO
        : ART_DOCUMENT_UPLOAD_TYPE.WEB_PHOTO;
};

export const getArtDocumentUploadEndpoint = (file, explicitType) => {
    const numericType = getArtDocumentUploadNumericType(file, explicitType);
    const batchID = makeArtUploadBatchId();
    return `documents/uploadsingle/${numericType}?batchID=${batchID}`;
};

export const uploadArtDocumentFile = async (file, uploadType) => {
    const uploadFile = resolveUploadFile(file);
    if (!uploadFile) {
        throw new Error('Invalid upload file');
    }

    const endpoint = getArtDocumentUploadEndpoint(uploadFile, uploadType);
    const formData = new FormData();
    formData.append('files', uploadFile);

    const headers = {};
    const currentUserId = getCurrentUserId();
    try {
        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        if (currentUser?.Token) {
            headers.Authorization = `Bearer ${currentUser.Token}`;
        }
    } catch (error) {
        if (!currentUserId) {
            throw new Error('Missing user session');
        }
    }

    // Use fetch (like Ant Design Upload) so the browser sets the multipart boundary.
    const response = await fetch(`${getBaseUrl()}/${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
    });

    if (!response.ok) {
        throw new Error('Upload failed');
    }

    const uploadPayload = normalizeDocumentResponse(await response.json());
    if (uploadPayload?.error) {
        throw new Error(uploadPayload.error);
    }

    const document = normalizeFetchedDocument(normalizeUploadedDocument(uploadPayload));
    if (!document) {
        throw new Error('Invalid upload response');
    }

    documentCache.set(document.ID, document);

    if (getArtDocumentUploadNumericType(uploadFile, uploadType) === ART_DOCUMENT_UPLOAD_TYPE.WEB_VIDEO) {
        const fetchedDocument = await getArtDocument(document.ID, { useCache: false });
        return fetchedDocument || document;
    }

    return document;
};

export const linkArtWebVideo = async ({ link, title }) => {
    const response = await Axios.post('/documents/LinkVideo', {
        link,
        title: title || 'Web video',
        type: ART_DOCUMENT_TYPE.WEB_VIDEO,
    });

    const linkedDocument = normalizeUploadedDocument(normalizeDocumentResponse(response));
    if (!linkedDocument) {
        throw new Error('Invalid link video response');
    }

    // LinkVideo returns only a document reference; load the full document for preview URLs.
    const document = await getArtDocument(linkedDocument.ID, { useCache: false });
    if (!document) {
        throw new Error('Unable to load linked video document');
    }

    return document;
};
